/**
 * <wine-lead-form> — client behavior for the custom "Sell My Wine" section.
 *
 * Validates the required email + the total attachment size (so the email stays
 * under the delivery cap), then posts the multipart form to the pervino-intake
 * app proxy (/apps/portal/sell-my-wine) via fetch and swaps in a success message. Falls back
 * to a native POST if this script doesn't run.
 *
 * Files accumulate across multiple "Choose Files" clicks (the native input
 * replaces its selection each time, so we keep our own DataTransfer, mirror it
 * back onto the input, and let the user remove individual files).
 */
if (!customElements.get('wine-lead-form')) {
  customElements.define(
    'wine-lead-form',
    class WineLeadForm extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.dataset.endpoint || '/apps/portal/sell-my-wine';
        this.maxBytes = parseInt(this.dataset.maxBytes, 10) || 15 * 1024 * 1024;
        this.maxMb = this.dataset.maxMb || Math.round(this.maxBytes / (1024 * 1024));

        this.form = this.querySelector('form');
        this.fileInput = this.querySelector('[data-files]');
        this.fileList = this.querySelector('[data-file-list]');
        this.filesTotal = this.querySelector('[data-files-total]');
        this.message = this.querySelector('[data-message]');
        this.submitBtn = this.querySelector('[data-submit]');
        this.successBox = this.querySelector('[data-success]');
        this.resetBtn = this.querySelector('[data-reset]');
        this.honeypot = this.querySelector('input[name="website"]');
        this.formTs = this.querySelector('[data-form-ts]');
        this.turnstile = this.querySelector('.cf-turnstile');
        if (!this.form) return;

        // Time-trap: stamp when the form became interactive. The server rejects
        // submits that arrive implausibly fast after this (see proxy.leads.tsx).
        this.stampFormTime();

        // Canonical list of chosen files, mirrored onto the input so a normal
        // FormData(form) submit includes every accumulated file.
        this.dt = new DataTransfer();

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        if (this.fileInput) this.fileInput.addEventListener('change', this.onFilesPicked.bind(this));
        if (this.fileList) this.fileList.addEventListener('click', this.onListClick.bind(this));
        if (this.resetBtn) this.resetBtn.addEventListener('click', this.startOver.bind(this));
      }

      stampFormTime() {
        if (this.formTs) this.formTs.value = String(Date.now());
      }

      get totalBytes() {
        let total = 0;
        for (const f of this.dt.files) total += f.size;
        return total;
      }

      formatMb(bytes) {
        return (bytes / (1024 * 1024)).toFixed(2);
      }

      // Merge the newly-picked files into our set (deduped), then mirror back.
      onFilesPicked() {
        const existing = new Set(Array.from(this.dt.files).map((f) => `${f.name}|${f.size}|${f.lastModified}`));
        for (const f of this.fileInput.files || []) {
          const key = `${f.name}|${f.size}|${f.lastModified}`;
          if (!existing.has(key)) {
            this.dt.items.add(f);
            existing.add(key);
          }
        }
        this.syncInput();
        this.renderFileList();
        if (this.totalBytes <= this.maxBytes) this.clearMessage();
      }

      removeFileAt(index) {
        this.dt.items.remove(index);
        this.syncInput();
        this.renderFileList();
        if (this.totalBytes <= this.maxBytes) this.clearMessage();
      }

      // Keep the native input's FileList equal to our DataTransfer.
      syncInput() {
        if (this.fileInput) this.fileInput.files = this.dt.files;
      }

      onListClick(event) {
        const btn = event.target.closest('[data-remove]');
        if (!btn) return;
        event.preventDefault();
        this.removeFileAt(parseInt(btn.dataset.remove, 10));
      }

      renderFileList() {
        const files = Array.from(this.dt.files);
        if (this.fileList) {
          if (files.length === 0) {
            this.fileList.hidden = true;
            this.fileList.innerHTML = '';
          } else {
            this.fileList.hidden = false;
            this.fileList.innerHTML = files
              .map(
                (f, i) =>
                  `<li><span class="wine-lead-form__file-name">${this.escape(f.name)} <span class="wine-lead-form__file-size">(${this.formatMb(f.size)} MB)</span></span>` +
                  `<button type="button" class="wine-lead-form__file-remove" data-remove="${i}" aria-label="Remove ${this.escape(f.name)}">&times;</button></li>`
              )
              .join('');
          }
        }
        this.updateTotal(files.length);
      }

      // Running "X.XX MB of Y MB selected." shown only when files are present.
      updateTotal(count) {
        if (!this.filesTotal) return;
        if (!count) {
          this.filesTotal.hidden = true;
          this.filesTotal.textContent = '';
          return;
        }
        const over = this.totalBytes > this.maxBytes;
        this.filesTotal.hidden = false;
        this.filesTotal.textContent =
          `${this.formatMb(this.totalBytes)} MB of ${this.maxMb} MB selected` +
          (over ? ' — over the limit, please remove some files.' : '.');
        this.filesTotal.classList.toggle('wine-lead-form__files-total--over', over);
      }

      showError(text) {
        if (!this.message) return;
        this.message.hidden = false;
        this.message.classList.add('wine-lead-form__message--error');
        this.message.textContent = text;
      }

      clearMessage() {
        if (!this.message) return;
        this.message.hidden = true;
        this.message.textContent = '';
      }

      // Turnstile tokens are single-use: after a rejected submit or "Submit
      // again", ask the widget for a fresh one so the next POST verifies.
      resetTurnstile() {
        if (this.turnstile && window.turnstile) {
          try {
            window.turnstile.reset(this.turnstile);
          } catch (e) {
            /* widget not ready yet — it'll mint a token on its own */
          }
        }
      }

      escape(s) {
        const d = document.createElement('div');
        d.textContent = s;
        return d.innerHTML;
      }

      // Lock every field (and the file picker) while the request is in flight.
      setControlsDisabled(disabled) {
        this.form.classList.toggle('wine-lead-form__form--sending', disabled);
        this.form.querySelectorAll('input, textarea, select, button').forEach((el) => {
          el.disabled = disabled;
        });
      }

      async onSubmit(event) {
        event.preventDefault();
        this.clearMessage();

        // Honeypot: a filled hidden field means a bot — pretend success, send nothing.
        if (this.honeypot && this.honeypot.value.trim() !== '') {
          this.showSuccess();
          return;
        }

        const email = this.form.querySelector('input[name="email"]')?.value.trim() || '';
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          this.showError('Please enter a valid email address so our team can reply to you.');
          this.form.querySelector('input[name="email"]')?.focus();
          return;
        }

        if (this.totalBytes > this.maxBytes) {
          this.showError(
            `Your attachments total ${this.formatMb(this.totalBytes)} MB, over the ${this.maxMb} MB limit. ` +
              `Please remove some files or send fewer at a time.`
          );
          return;
        }

        // Snapshot the form data BEFORE disabling controls — disabled fields are
        // excluded from FormData, which would submit an empty form.
        const body = new FormData(this.form);
        const original = this.submitBtn?.textContent;
        this.setControlsDisabled(true);
        if (this.submitBtn) this.submitBtn.textContent = 'Sending…';

        try {
          const res = await fetch(this.endpoint, {
            method: 'POST',
            body,
            headers: { Accept: 'application/json' },
          });
          let data = {};
          try {
            data = await res.json();
          } catch (e) {
            /* non-JSON response */
          }
          if (res.ok && data.ok) {
            this.showSuccess();
          } else {
            this.showError(
              data.error || 'Sorry, something went wrong sending your submission. Please try again.'
            );
            this.resetTurnstile();
          }
        } catch (e) {
          this.showError('Network error — please check your connection and try again.');
          this.resetTurnstile();
        } finally {
          this.setControlsDisabled(false);
          if (this.submitBtn) this.submitBtn.textContent = original;
        }
      }

      // Success: clear + hide the form, leaving only the heading/subheading and
      // the success notice (with an optional "Submit again" button).
      showSuccess() {
        this.clearMessage();
        this.form.reset();
        this.dt = new DataTransfer();
        this.syncInput();
        this.renderFileList();
        this.form.hidden = true;
        if (this.successBox) {
          this.successBox.hidden = false;
          this.successBox.setAttribute('tabindex', '-1');
          this.successBox.focus();
        }
      }

      // "Submit again": hide the notice and bring back a fresh (already-reset) form.
      startOver() {
        if (this.successBox) this.successBox.hidden = true;
        this.form.hidden = false;
        // form.reset() blanked the stamp — restart the clock for the new submit.
        this.stampFormTime();
        // The previous token was consumed by the successful submit — get a fresh one.
        this.resetTurnstile();
        this.form.querySelector('input, textarea')?.focus();
      }
    }
  );
}
